<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useMutation } from '@tanstack/vue-query'
import { Eye, EyeOff, Fingerprint, KeyRound, Loader2 } from 'lucide-vue-next'
import Input from '@/components/ui/Input.vue'
import Button from '@/components/ui/Button.vue'
import { useAuthStore } from '@/stores/auth'
import { login as apiLogin } from '@/api/auth'
import { useToast } from '@/composables/useToast'
import { useBackendVersion } from '@/composables/useBackendVersion'
import {
  getPasskey,
  passkeyBeginLogin,
  passkeyFinishLogin,
  passkeyServerAvailable,
  probePasskeySupport,
  type PasskeySupport,
} from '@/api/passkey'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const toast = useToast()

// v3.14.0 (安全打磨): 登录后重定向仅允许站内路径 — 防开放重定向
// (?redirect=https://evil.com 或 //evil.com)。hash 路由下外链 largely
// 失效, 但协议相对路径 (//evil.com) 与编码变体仍需显式拒绝。
function sanitizeRedirect(raw: string | undefined): string {
  const fallback = '/dashboard'
  if (!raw) return fallback
  if (!raw.startsWith('/')) return fallback
  if (raw.startsWith('//')) return fallback
  if (raw.includes('\\')) return fallback
  return raw
}

// 登录卡副标题: 节点名 + 后端真实版本 (全局缓存, 只请求一次)
const { version: backendVersion, nodeName } = useBackendVersion()
const loginSubtitle = computed(() =>
  [nodeName.value, backendVersion.value].filter(Boolean).join(' · '),
)

// v3.13.40: 默认密码登录; 通行密钥仅在"环境支持 + 服务端已注册"时才出现入口
const usePasskeyMode = ref(false)
const passkeySupport = ref<PasskeySupport>({ available: false })
const passkeyConfigured = ref(false) // 服务端是否已有注册的通行密钥

// 两者都满足才显示通行密钥 tab — 没配置过密钥时登录页只有密码表单
const passkeyReady = computed(() => passkeySupport.value.available && passkeyConfigured.value)

onMounted(async () => {
  passkeySupport.value = await probePasskeySupport()
  passkeyConfigured.value = await passkeyServerAvailable()
  if (!passkeyReady.value) {
    usePasskeyMode.value = false
  }
})

const form = reactive({
  username: '',
  password: '',
  remember: true,
})

const showPwd = ref(false)
const errorMsg = ref('')
const usernameError = ref('')
const passwordError = ref('')

const mutation = useMutation({
  mutationFn: (vars: { username: string; password: string }) => apiLogin(vars),
  onSuccess: (resp) => {
    auth.setAuth(resp.user, resp.token)
    toast.success(t('auth.loginButton') + ' ✓', 'AFD Console')
    const redirect = sanitizeRedirect((route.query.redirect as string | undefined))
    router.push(redirect).catch(() => {
      /* ignore */
    })
  },
  onError: (err: Error) => {
    errorMsg.value = err.message || t('auth.invalidCredentials')
    toast.error(errorMsg.value)
  },
})

const submitDisabled = computed(() => mutation.isPending.value || pkMutation.isPending.value)

function submit(e: Event): void {
  e.preventDefault()
  errorMsg.value = ''
  usernameError.value = ''
  passwordError.value = ''
  if (!form.username) {
    usernameError.value = t('auth.loginRequired')
  }
  if (!form.password) {
    passwordError.value = t('auth.loginRequired')
  }
  if (!form.username || !form.password) return
  mutation.mutate({ username: form.username, password: form.password })
}

// v3.13.39: 通行密钥登录 — usernameless discoverable flow.
// 不传 username → 服务端 allowCredentials=[] → 浏览器弹所有该 rp.id 下已注册密钥
// → 选中后浏览器自动填回 userHandle → 服务端 finish-login 用 cred.Label 反解用户名.
const pkMutation = useMutation({
  mutationFn: async () => {
    const begin = await passkeyBeginLogin()
    return await getPasskey(begin)
  },
  onSuccess: async (raw) => {
    const finish = await passkeyFinishLogin(raw)
    if (!finish.ok) {
      throw new Error('passkey login failed')
    }
    auth.setAuth(finish.user, finish.token.value)
    toast.success(t('auth.loginButton') + ' ✓', 'AFD Console')
    const redirect = sanitizeRedirect((route.query.redirect as string | undefined))
    router.push(redirect).catch(() => {
      /* ignore */
    })
  },
  onError: (err: Error) => {
    // 用户取消 (NotAllowedError) 不报错; 其它失败才 toast
    if (err.name !== 'NotAllowedError') {
      toast.error(err.message || t('auth.passkeyFailed'))
    }
  },
})

async function loginWithPasskey(e: Event): Promise<void> {
  e.preventDefault()
  if (!passkeyReady.value) {
    toast.error(t('auth.passkeyUnsupported'))
    return
  }
  pkMutation.mutate(undefined)
}

function switchTo(mode: 'password' | 'passkey'): void {
  usePasskeyMode.value = mode === 'passkey'
  errorMsg.value = ''
}
</script>

<template>
  <div class="relative flex min-h-screen items-center justify-center overflow-hidden p-4">
    <!-- v2: 光斑背景 + 玻璃登录卡 -->
    <div class="bg-orbs" aria-hidden="true"></div>
    <div class="glass-panel relative z-10 w-full max-w-sm rounded-3xl p-8">
      <div class="mb-6 flex flex-col items-center gap-2 text-center">
        <div
          class="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-[hsl(258_90%_67%)] text-lg font-extrabold text-white shadow-[0_8px_24px_rgba(77,107,254,0.4)]"
          aria-hidden="true"
        >
          AF
        </div>
        <h2 class="text-lg font-bold tracking-wide text-fg">AFD Console</h2>
        <!-- 真实节点名 + 版本 (/api/system-info) — 之前硬编码 "vm00 · 深圳 · 3.13.38" 导致升级后版本显示永远不变 -->
        <p v-if="loginSubtitle" class="text-xs text-fg-muted">{{ loginSubtitle }}</p>
      </div>

      <!-- Visible inline error + aria-live announcement -->
      <div
        v-if="errorMsg"
        role="alert"
        aria-live="assertive"
        class="mb-4 flex items-start gap-2 rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger"
      >
        <span class="font-medium">⚠</span>
        <span>{{ errorMsg }}</span>
      </div>

      <!-- 模式切换 (密码 vs 通行密钥) — 未注册通行密钥或环境不支持时不渲染, 只留密码表单 -->
      <div v-if="passkeyReady" class="mb-4 flex rounded-md bg-bg-elevated/40 p-0.5">
        <button
          type="button"
          class="flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors"
          :class="!usePasskeyMode ? 'bg-brand-500 text-white shadow-sm' : 'text-fg-muted hover:text-fg'"
          @click="switchTo('password')"
        >
          {{ t('auth.tab.password') }}
        </button>
        <button
          type="button"
          class="flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors"
          :class="usePasskeyMode ? 'bg-brand-500 text-white shadow-sm' : 'text-fg-muted hover:text-fg'"
          @click="switchTo('passkey')"
        >
          <KeyRound :size="12" class="-mt-0.5 mr-1 inline align-middle" />
          {{ t('auth.tab.passkey') }}
        </button>
      </div>

      <!-- 密码登录表单 -->
      <form v-if="!usePasskeyMode" class="space-y-4" novalidate @submit="submit">
        <div>
          <label
            for="login-username"
            class="mb-1 block text-xs font-medium text-fg-muted"
          >
            {{ t('auth.username') }}
          </label>
          <Input
            id="login-username"
            v-model="form.username"
            type="text"
            :placeholder="t('auth.username')"
            autocomplete="username"
            :error="usernameError"
            :disabled="submitDisabled"
            required
          />
        </div>

        <div>
          <label
            for="login-password"
            class="mb-1 block text-xs font-medium text-fg-muted"
          >
            {{ t('auth.password') }}
          </label>
          <div class="relative">
            <Input
              id="login-password"
              v-model="form.password"
              :type="showPwd ? 'text' : 'password'"
              :placeholder="t('auth.password')"
              autocomplete="current-password"
              :error="passwordError"
              :disabled="submitDisabled"
              required
            />
            <button
              type="button"
              :aria-label="showPwd ? t('auth.hidePassword') : t('auth.showPassword')"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded p-1"
              @click="showPwd = !showPwd"
            >
              <component :is="showPwd ? EyeOff : Eye" :size="14" />
            </button>
          </div>
        </div>

        <label class="flex items-center gap-2 text-xs text-fg-muted">
          <input
            v-model="form.remember"
            type="checkbox"
            class="rounded border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {{ t('auth.rememberMe') }}
        </label>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          :block="true"
          :loading="mutation.isPending.value"
          :disabled="submitDisabled"
        >
          <template v-if="mutation.isPending.value" #icon>
            <Loader2 :size="14" class="animate-spin" />
          </template>
          {{ mutation.isPending.value ? t('auth.loggingIn') : t('auth.loginButton') }}
        </Button>
      </form>

      <!-- 通行密钥登录 — 仅在 passkeyReady 时可达 (tab 已按可用性渲染) -->
      <form v-else class="space-y-4" novalidate @submit="loginWithPasskey">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          :block="true"
          :loading="pkMutation.isPending.value"
          :disabled="submitDisabled || !passkeyReady"
        >
          <template v-if="pkMutation.isPending.value" #icon>
            <Loader2 :size="14" class="animate-spin" />
          </template>
          <template v-else #icon>
            <Fingerprint :size="16" />
          </template>
          {{ pkMutation.isPending.value ? t('auth.touchKey') : t('auth.loginWithPasskey') }}
        </Button>
        <p class="text-xs text-fg-subtle text-center">
          {{ t('auth.passkeyHint') }}
        </p>
      </form>
    </div>
  </div>
</template>
